// ============================================================================
// TENANT HOOKS
// ============================================================================
// React hooks for Tenant Management API operations

import { useState, useEffect, useCallback, useMemo } from 'react';
import { tenantService } from 'api/services';
import type {
  TenantResponse,
  TenantSummaryResponse,
  CreateTenantRequest,
  UpdateTenantRequest,
  SuspendTenantRequest,
  TenantHubPagedRequest,
  TenantStatistics,
  ID,
} from 'api/types';

/**
 * Hook to fetch all tenants (paginated)
 */
export const useTenants = (params?: TenantHubPagedRequest) => {
  const [data, setData] = useState<TenantResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Stable dep key so inline object-literal params (`useTenants({ pageNumber: 1, pageSize: 500 })`)
  // don't recreate fetchTenants → useEffect → setState → re-render → infinite loop.
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantService.getAll(params);
      const pagedResult = response.data;
      setData(pagedResult.items);
      setPagination({
        currentPage: pagedResult.pageNumber,
        pageSize: pagedResult.pageSize,
        totalPages: pagedResult.totalPages,
        totalItems: pagedResult.totalCount,
        hasNextPage: pagedResult.hasNextPage,
        hasPreviousPage: pagedResult.hasPreviousPage,
      });
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchTenants,
  };
};

/**
 * Hook to fetch a single tenant by ID
 */
export const useTenant = (id: ID) => {
  const [data, setData] = useState<TenantResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTenant = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await tenantService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  return {
    data,
    loading,
    error,
    refetch: fetchTenant,
  };
};

/**
 * Hook to fetch tenant summaries (shared cache — multiple callers reuse the same fetch)
 */
let _summariesCache: TenantSummaryResponse[] | null = null;
let _summariesPromise: Promise<TenantSummaryResponse[]> | null = null;
let _summariesListeners: Set<() => void> = new Set();

const fetchSummariesShared = async (): Promise<TenantSummaryResponse[]> => {
  if (_summariesPromise) return _summariesPromise;
  _summariesPromise = tenantService.getSummaries().then(res => {
    _summariesCache = res.data;
    _summariesPromise = null;
    _summariesListeners.forEach(cb => cb());
    return res.data;
  }).catch(err => {
    _summariesPromise = null;
    throw err;
  });
  return _summariesPromise;
};

export const useTenantSummaries = () => {
  const [data, setData] = useState<TenantSummaryResponse[]>(_summariesCache ?? []);
  const [loading, setLoading] = useState(!_summariesCache);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Subscribe to cache updates from other callers
    const onUpdate = () => { if (_summariesCache) setData(_summariesCache); };
    _summariesListeners.add(onUpdate);

    if (_summariesCache) {
      setData(_summariesCache);
      setLoading(false);
    } else {
      setLoading(true);
      fetchSummariesShared()
        .then(result => { setData(result); setLoading(false); })
        .catch(err => { setError(err as Error); setLoading(false); });
    }

    return () => { _summariesListeners.delete(onUpdate); };
  }, []);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    _summariesCache = null; // Invalidate cache
    try {
      const result = await fetchSummariesShared();
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, refetch };
};

/**
 * Hook to create a new tenant
 */
export const useCreateTenant = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createTenant = async (
    data: CreateTenantRequest,
    options?: {
      onSuccess?: (tenant: TenantResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await tenantService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createTenant,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a tenant
 */
export const useUpdateTenant = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateTenant = async (
    id: ID,
    data: UpdateTenantRequest,
    options?: {
      onSuccess?: (tenant: TenantResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await tenantService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateTenant,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a tenant
 */
export const useDeleteTenant = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteTenant = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await tenantService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteTenant,
    loading,
    error,
    success,
  };
};

/**
 * Hook to suspend a tenant
 */
export const useSuspendTenant = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const suspendTenant = async (
    id: ID,
    data: SuspendTenantRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await tenantService.suspend(id, data);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    suspendTenant,
    loading,
    error,
    success,
  };
};

/**
 * Hook to activate a tenant
 */
export const useActivateTenant = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const activateTenant = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await tenantService.activate(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    activateTenant,
    loading,
    error,
    success,
  };
};

/**
 * Hook to fetch tenant statistics
 */
export const useTenantStatistics = () => {
  const [data, setData] = useState<TenantStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantService.getStatistics();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    data,
    loading,
    error,
    refetch: fetchStatistics,
  };
};
